import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, UniqueIdentifier } from "@dnd-kit/core";
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import * as Icon from 'react-bootstrap-icons';
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../app/store";
import { useAuth } from "../signin/auth";
import ChecklistItemCompletedInput from "./ChecklistItemCompletedInput";
import { orderChecklistItems } from "./checklistSlice";
import styles from './ListChecklistItems.module.css';
import { ChecklistItem } from "./types";

interface ListChecklistItemsProps {
  checklistID: string;
  items: Array<ChecklistItem>;
  showDelete: Boolean;
  onItemCompleteChecked: (completed: Boolean) => void;
  onItemDeleteClicked: (checklistItemID: string) => void;
}

ListChecklistItems.defaultProps = {
  showDelete: false,
  onItemCompleteChecked: () => { },
  onItemDeleteClicked: () => { },
};

interface ItemIndexMap {
  [key: UniqueIdentifier]: number;
}

function ListChecklistItems(props: ListChecklistItemsProps) {
  const auth = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const { items, checklistID, onItemCompleteChecked, onItemDeleteClicked, showDelete } = props;
  const [sortItems, setSortItems] = useState<Array<UniqueIdentifier>>([]);
  const [itemIndexMap, setItemIndexMap] = useState<ItemIndexMap>({});
  const [activeID, setActiveID] = useState<UniqueIdentifier | null>(null);
  const [wasSorted, setWasSorted] = useState<Boolean>(false);

  useEffect(() => {
    setSortItems(items.map((i) => i.id as UniqueIdentifier));
    setItemIndexMap(Object.fromEntries(items.map((item, index) => [item.id as UniqueIdentifier, index])));
  }, [items])

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveID(active.id);
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setSortItems((sortItems) => {
        const oldIndex = sortItems.indexOf(active.id);
        const newIndex = sortItems.indexOf(over?.id || "");
        return arrayMove(sortItems, oldIndex, newIndex);
      });
      setWasSorted(true);
    }
    setActiveID(null);
  }

  useEffect(() => {
    if (wasSorted && auth.authToken) {
      dispatch(orderChecklistItems({
        authToken: auth.authToken,
        checklistID: checklistID,
        checklistItemIDs: sortItems.map((i) => i.toString()),
      }))
    }
    setWasSorted(false);
  }, [auth.authToken, dispatch, sortItems, wasSorted, checklistID]);

  const SortableItem = (props: { item: ChecklistItem }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({ id: props.item.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div className="d-flex flex-row align-items-center" ref={setNodeRef} style={style}>
        <div className={`flex-fill py-1 ${styles.draggableRegion}`} {...listeners} {...attributes}>
          <Icon.GripVertical className={styles.dragHandle} {...attributes} {...listeners} />
          {props.item.name}
        </div>
        <div className="py-1">
          <ChecklistItemCompletedInput className={`me-2 ${styles.checkbox}`} item={props.item} onChange={onItemCompleteChecked} />
        </div>
        {showDelete && (
          <div className="ms-4 me-2 py-1">
            <Button variant="outline-danger" size="sm" className="ms-auto" onClick={() => onItemDeleteClicked(props.item.id)}>Delete</Button>
          </div>
        )}
      </div>
    );
  }

  const ItemProxy = (props: { item: ChecklistItem }) => {
    return (
      <div className={`d-flex flex-row align-items-center ${styles.checklistItemProxy}`}>
        <div className="flex-fill py-1">
          <Icon.GripVertical className={styles.dragHandle} />
          {props.item.name}
        </div>
        <div className="py-1">
          <ChecklistItemCompletedInput className={`me-2 ${styles.checkbox}`} item={props.item} disabled />
        </div>
      </div>
    );
  };

  return (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <SortableContext items={sortItems} strategy={verticalListSortingStrategy}>
        <div>
          {sortItems.map((id, index) => {
            const item = items[itemIndexMap[id]];
            return (<div key={`${item.id}-sortable`}>
              <SortableItem item={item} />
              {index < sortItems.length - 1 ? <div className={styles.checklistItemDivider} /> : null}
            </div>);
          })}
        </div>
      </SortableContext>
      <DragOverlay modifiers={[restrictToVerticalAxis]}>
        {activeID ? <ItemProxy item={items[itemIndexMap[activeID]]} /> : null}
      </DragOverlay>
    </DndContext >
  )
}

export default ListChecklistItems;
